import appVariables from '@/config'
import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { numberFormat } from '@/helpers/numbers'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  CardMedia,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { formatTimeslotTime } from '../timeslotDates'

const canteenOrderedItemCardOrderItemFragment = graphql(`
  fragment CanteenOrderedItemCard_OrderItemFragmentV2 on CCOrderItem {
    id
    name
    qty
    unitPrice
    ccSupplierOrder {
      arrivalTime
    }
    vendor {
      companyName
      logo
    }
    accountLocation {
      timezone
      id
      label
    }
    canCancel
    liveMenuItem {
      coreLiveItem {
        _id
        image
      }
    }
  }
`)

type OrderedItemCardMediaProps = {
  imageUrl?: string | null
  imageTitle?: string | null
  vendorName?: string | null
  vendorAvatar?: string | null
}

const OrderedItemCardMedia: React.FC<OrderedItemCardMediaProps> = ({
  imageTitle,
  imageUrl,
  vendorName,
  vendorAvatar,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
  })
  const url = imageUrl
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: imageUrl,
        width: 240,
        quality: 80,
      })
    : NoImageAvailable.src

  const avatarUrl = getCloudflareImageUrl({
    enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
    src: vendorAvatar || '',
    width: 12,
    quality: 100,
  })

  const title = imageTitle || ''
  return (
    <CardMedia
      sx={{ width: 120, height: 120, minWidth: 120, minHeight: 120, borderRadius: 1 }}
      image={inView ? url : ''}
      title={title}
      ref={ref}>
      {vendorAvatar && vendorName ? (
        <Stack padding={1}>
          <Tooltip title={vendorName}>
            <Avatar sx={{ width: 12, height: 12 }} alt={vendorName} src={inView ? avatarUrl : ''}>
              &nbsp;
            </Avatar>
          </Tooltip>
        </Stack>
      ) : null}
    </CardMedia>
  )
}

interface CCOrderedItemCardV2Props {
  orderItem: FragmentType<typeof canteenOrderedItemCardOrderItemFragment>
  hidePrices: boolean
  cancelOrder: (ccOrderItemId: number) => void
  showItemDetails: (itemId: string) => void
  pendingCancellation?: boolean
}

export function CCOrderedItemCardV2({
  orderItem: orderItemProps,
  hidePrices,
  cancelOrder,
  showItemDetails,
  pendingCancellation,
}: CCOrderedItemCardV2Props) {
  const { t } = useTranslation()
  const orderItem = useFragment(canteenOrderedItemCardOrderItemFragment, orderItemProps)
  const { id, vendor, liveMenuItem, name, qty, unitPrice, accountLocation, ccSupplierOrder } =
    orderItem

  const itemCountTranslation = qty === 1 ? 'itemCountSingular' : 'itemCountPlural'
  const itemsCountText = t(`cc:${itemCountTranslation}`, { count: qty })

  const detailsText = [
    accountLocation.label,
    formatTimeslotTime(ccSupplierOrder?.arrivalTime, accountLocation.timezone),
    itemsCountText,
  ].join(' · ')

  const formattedPrice = useMemo(() => {
    return numberFormat(qty * unitPrice)
  }, [qty, unitPrice])

  return (
    <Card
      sx={{
        padding: 0.5,
        position: 'relative',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflowWrap: 'anywhere',
        opacity: pendingCancellation ? 0.5 : 1,
        pointerEvents: pendingCancellation ? 'none' : 'auto',
        transition: 'opacity 0.3s ease',
      }}
      onClick={(e) => {
        e.preventDefault()
        showItemDetails(id.toString())
      }}>
      {pendingCancellation && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}>
          <CircularProgress size={24} />
        </Box>
      )}
      <Stack direction="row" spacing={1} minHeight={120}>
        <OrderedItemCardMedia
          vendorAvatar={vendor.logo}
          vendorName={vendor.companyName}
          imageTitle={name}
          imageUrl={liveMenuItem.coreLiveItem?.image || ''}
        />
        <Stack
          direction="column"
          justifyContent="space-between"
          width="100%"
          height="100%"
          minHeight={120}>
          <CardHeader title={name} sx={{ padding: 0 }} />
          <Typography variant="body2" color="text.secondary">
            {detailsText}
          </Typography>
          <Stack
            direction="row-reverse"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap-reverse">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                cancelOrder(orderItem.id)
              }}
              disabled={!orderItem.canCancel}
              size="medium"
              variant="text"
              color="error"
              sx={{ wordWrap: 'normal' }}>
              {t('common:cancel')}
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault()
                showItemDetails(id.toString())
              }}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ wordWrap: 'normal', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
              {t('cc:showMore')}
            </Button>
            {!hidePrices ? (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {`${formattedPrice} Credits`}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}
